import React from 'react';
import { Box, Typography, LinearProgress, Chip } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import { useTranslation } from 'react-i18next';

/**
 * FrequencyCell Component
 * Compact visualization for table showing case coverage and station count
 *
 * @param {object} props
 * @param {number|null} props.caseCoverage - Percentage of cases (0-100)
 * @param {number|null} props.stationCount - Number of stations
 */
const FrequencyCell = ({ caseCoverage = null, stationCount = null }) => {
  const { t } = useTranslation();
  const hasCaseCoverage = Number.isFinite(caseCoverage);
  const hasStationCount = Number.isFinite(stationCount);
  const progressValue = hasCaseCoverage
    ? Math.min(100, Math.max(0, caseCoverage))
    : 0;

  return (
    <Box sx={{ minWidth: 160 }}>
      {/* Case Coverage Bar */}
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
          <PeopleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
            {t('dataCatalog.frequency.cases')}
          </Typography>
          <Typography variant="caption" fontWeight="bold" sx={{ ml: 'auto', fontSize: '0.75rem' }}>
            {hasCaseCoverage ? `${caseCoverage}%` : '–'}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressValue}
          sx={{
            height: 6,
            borderRadius: 1,
            backgroundColor: '#e0e0e0',
            '& .MuiLinearProgress-bar': {
              backgroundColor: !hasCaseCoverage
                ? '#bdbdbd'
                : caseCoverage >= 70
                  ? '#4caf50'
                  : caseCoverage >= 40
                    ? '#ff9800'
                    : '#2196f3',
              borderRadius: 1
            }
          }}
        />
      </Box>

      {/* Station Count */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <BusinessIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
          {t('dataCatalog.frequency.stations')}
        </Typography>
        <Chip
          label={hasStationCount ? stationCount : '–'}
          size="small"
          sx={{
            ml: 'auto',
            height: 18,
            fontSize: '0.7rem',
            fontWeight: 'bold',
            backgroundColor: !hasStationCount
              ? '#eeeeee'
              : stationCount >= 10
                ? '#e8f5e9'
                : stationCount >= 5
                  ? '#fff3e0'
                  : '#e3f2fd',
            color: !hasStationCount
              ? '#616161'
              : stationCount >= 10
                ? '#2e7d32'
                : stationCount >= 5
                  ? '#e65100'
                  : '#1565c0'
          }}
        />
      </Box>
    </Box>
  );
};

export default FrequencyCell;
